import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError, formatINR } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, Ban, Trash2, Users, Package, Truck, UserRound,
  CheckCircle2, XCircle, Search, ArrowRight, FileText, Clock, ShieldAlert, Eye,
  Pencil, UserX,
} from "lucide-react";

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [loads, setLoads] = useState([]);
  const [docs, setDocs] = useState([]);
  const [docFilter, setDocFilter] = useState("pending");
  const [q, setQ] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [blackUser, setBlackUser] = useState(null);
  const [blackReason, setBlackReason] = useState("");

  const fetchOverview = async () => {
    try { const { data } = await api.get("/admin/overview"); setOverview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const fetchUsers = async (role = "") => {
    try {
      const url = role ? `/admin/users?role=${role}` : "/admin/users";
      const { data } = await api.get(url);
      setUsers(data.users);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const fetchLoads = async () => {
    try { const { data } = await api.get("/admin/loads"); setLoads(data.loads); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const fetchDocs = async (status = "pending") => {
    try {
      const url = status === "all" ? "/admin/documents" : `/admin/documents?status=${status}`;
      const { data } = await api.get(url);
      setDocs(data.documents);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { fetchOverview(); fetchUsers(); fetchLoads(); fetchDocs("pending"); }, []);
  useEffect(() => { fetchUsers(userRole); }, [userRole]);
  useEffect(() => { fetchDocs(docFilter); }, [docFilter]);

  const toggleVerify = async (uid) => {
    try { await api.post(`/admin/users/${uid}/verify`); toast.success("Verification toggled"); fetchUsers(userRole); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleBan = async (uid) => {
    try { await api.post(`/admin/users/${uid}/ban`); toast.success("Ban toggled"); fetchUsers(userRole); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveUserEdit = async () => {
    try {
      const payload = { ...editUser };
      if ("experience_years" in payload) payload.experience_years = Number(payload.experience_years || 0);
      await api.patch(`/admin/users/${editUser.id}`, payload);
      toast.success("User updated"); setEditUser(null); fetchUsers(userRole);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirmBlacklist = async () => {
    if (!blackReason || blackReason.trim().length < 5) { toast.error("Reason must be at least 5 characters"); return; }
    try {
      await api.post(`/admin/users/${blackUser.id}/blacklist`, { reason: blackReason.trim() });
      toast.success("User blacklisted"); setBlackUser(null); setBlackReason(""); fetchUsers(userRole);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const unblacklist = async (uid) => {
    try { await api.post(`/admin/users/${uid}/unblacklist`); toast.success("Blacklist removed"); fetchUsers(userRole); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteLoad = async (id) => {
    if (!window.confirm("Delete this load and all bids/messages? This cannot be undone.")) return;
    try { await api.delete(`/admin/loads/${id}`); toast.success("Deleted"); fetchLoads(); fetchOverview(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uq = q.trim().toLowerCase();
  const filteredUsers = uq ? users.filter(u => [u.name, u.email, u.phone, u.city].join(" ").toLowerCase().includes(uq)) : users;
  const filteredLoads = uq ? loads.filter(l => [l.title, l.source_city, l.destination_city, l.sender_name].join(" ").toLowerCase().includes(uq)) : loads;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="overline text-[#FF5A00]">NORTHLINE CONTROL ROOM</div>
        <h1 className="font-display font-black text-4xl mt-2 tracking-tight" data-testid="admin-dashboard">Manage the whole yard.</h1>

        {overview && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat icon={Users} label="Total users" value={overview.users.total} />
            <Stat icon={Package} label="Senders" value={overview.users.senders} />
            <Stat icon={Truck} label="Lorry Owners" value={overview.users.lorry_owners} />
            <Stat icon={UserRound} label="Drivers" value={overview.users.drivers} />
            <Stat label="Loads total" value={overview.loads.total} accent />
            <Stat label="Active" value={overview.loads.active} />
            <Stat label="Confirmed" value={overview.loads.confirmed} />
            <Stat label="Delivered" value={overview.loads.delivered} />
          </div>
        )}

        <div className="relative max-w-md mt-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search-input"
            placeholder="Search users, loads..."
            className="h-11 pl-10 bg-[#141414] border-[#2A2A2A] focus-visible:ring-[#FF5A00] rounded-sm" />
        </div>

        <Tabs defaultValue="users" className="mt-8">
          <TabsList className="bg-[#141414] border border-[#2A2A2A] rounded-sm p-1">
            <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Users</TabsTrigger>
            <TabsTrigger value="loads" data-testid="admin-tab-loads" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Loads</TabsTrigger>
            <TabsTrigger value="docs" data-testid="admin-tab-docs" className="rounded-sm data-[state=active]:bg-[#FF5A00] data-[state=active]:text-black">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { k: "", l: "All" },
                { k: "sender", l: "Senders" },
                { k: "lorry_owner", l: "Lorry Owners" },
                { k: "driver", l: "Drivers" },
              ].map((f) => (
                <button key={f.k} onClick={() => setUserRole(f.k)} data-testid={`admin-filter-${f.k || "all"}`}
                  className={`px-3 py-1 text-xs uppercase tracking-wider border rounded-sm ${userRole === f.k ? "border-[#FF5A00] bg-[#FF5A00]/10 text-[#FF5A00]" : "border-[#2A2A2A] text-zinc-400 hover:text-white"}`}>
                  {f.l}
                </button>
              ))}
            </div>
            <div className="border border-[#2A2A2A] rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#141414] text-zinc-400 uppercase tracking-wider text-xs">
                  <tr>
                    <th className="text-left p-3">User</th>
                    <th className="text-left p-3">Role</th>
                    <th className="text-left p-3">Contact</th>
                    <th className="text-left p-3">Details</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-[#2A2A2A] hover:bg-[#141414]/60">
                      <td className="p-3">
                        <div className="font-semibold">{u.name}</div>
                        <div className="text-xs text-zinc-500">{u.company || "—"}</div>
                      </td>
                      <td className="p-3"><RoleTag role={u.role} /></td>
                      <td className="p-3 font-mono-ll text-xs">
                        <div>{u.email}</div>
                        <div className="text-zinc-500">{u.phone}</div>
                      </td>
                      <td className="p-3 text-xs text-zinc-400">
                        {u.role === "lorry_owner" && u.vehicle && (
                          <>{u.vehicle.type || "—"} · {u.vehicle.number || "—"} · {u.vehicle.capacity_kg || 0}kg</>
                        )}
                        {u.role === "driver" && u.driver_profile && (
                          <>DL {u.driver_profile.dl_number || "—"} · {u.driver_profile.home_city}, {u.driver_profile.home_state} · {u.driver_profile.experience_years}y exp</>
                        )}
                        {u.role === "sender" && <>{u.city || "—"}</>}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-col gap-1">
                          {u.verified ? <span className="text-emerald-300 text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Verified</span> : <span className="text-zinc-500 text-xs">Unverified</span>}
                          {u.blacklisted
                            ? <span data-testid={`user-blacklisted-${u.id}`} className="text-rose-300 text-xs inline-flex items-center gap-1" title={u.blacklist_reason}><UserX className="w-3 h-3" /> Blacklisted</span>
                            : (u.banned ? <span className="text-red-400 text-xs inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Banned</span> : null)}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-2 flex-wrap justify-end">
                          <Button size="sm" variant="outline" onClick={() => toggleVerify(u.id)}
                            data-testid={`admin-verify-${u.id}`}
                            className="h-8 rounded-sm border-[#2A2A2A] bg-transparent hover:bg-[#FF5A00]/10">
                            <ShieldCheck className="w-3 h-3 mr-1" /> {u.verified ? "Unverify" : "Verify"}
                          </Button>
                          {u.role === "driver" && (
                            <Button size="sm" variant="outline" onClick={() => setEditUser({
                              id: u.id, name: u.name || "", phone: u.phone || "", city: u.city || "",
                              dl_number: u.driver_profile?.dl_number || "",
                              home_city: u.driver_profile?.home_city || "",
                              home_state: u.driver_profile?.home_state || "",
                              experience_years: u.driver_profile?.experience_years || 0,
                            })} data-testid={`admin-edit-driver-${u.id}`}
                              className="h-8 rounded-sm border-[#2A2A2A] bg-transparent hover:bg-sky-500/10 text-sky-400">
                              <Pencil className="w-3 h-3 mr-1" /> Edit
                            </Button>
                          )}
                          {u.role !== "admin" && u.role !== "super_admin" && (
                            u.blacklisted
                              ? <Button size="sm" variant="outline" onClick={() => unblacklist(u.id)}
                                  data-testid={`admin-unblack-${u.id}`}
                                  className="h-8 rounded-sm border-[#2A2A2A] bg-transparent text-emerald-300 hover:bg-emerald-900/20">
                                  <UserX className="w-3 h-3 mr-1" /> Remove blacklist
                                </Button>
                              : <Button size="sm" variant="outline" onClick={() => { setBlackUser(u); setBlackReason(""); }}
                                  data-testid={`admin-blacklist-${u.id}`}
                                  className="h-8 rounded-sm border-[#2A2A2A] bg-transparent text-rose-400 hover:bg-rose-900/20">
                                  <UserX className="w-3 h-3 mr-1" /> Blacklist
                                </Button>
                          )}
                          {u.role !== "admin" && !u.blacklisted && (
                            <Button size="sm" variant="outline" onClick={() => toggleBan(u.id)}
                              data-testid={`admin-ban-${u.id}`}
                              className={`h-8 rounded-sm border-[#2A2A2A] bg-transparent ${u.banned ? "text-emerald-300 hover:bg-emerald-900/20" : "text-red-400 hover:bg-red-900/20"}`}>
                              <Ban className="w-3 h-3 mr-1" /> {u.banned ? "Unban" : "Ban"}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No users found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="loads" className="mt-6">
            <div className="border border-[#2A2A2A] rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#141414] text-zinc-400 uppercase tracking-wider text-xs">
                  <tr>
                    <th className="text-left p-3">Load</th>
                    <th className="text-left p-3">Route</th>
                    <th className="text-left p-3">Kind</th>
                    <th className="text-left p-3">Sender</th>
                    <th className="text-left p-3">Budget / Final</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLoads.map((l) => (
                    <tr key={l.id} data-testid={`admin-load-row-${l.id}`} className="border-t border-[#2A2A2A] hover:bg-[#141414]/60">
                      <td className="p-3">
                        <div className="font-semibold">{l.title}</div>
                        <div className="text-xs text-zinc-500 font-mono-ll">#{l.id.slice(0, 8)}</div>
                      </td>
                      <td className="p-3 font-mono-ll text-xs inline-flex items-center gap-2">
                        <span>{l.source_city}</span><ArrowRight className="w-3 h-3 text-[#FF5A00]" /><span>{l.destination_city}</span>
                      </td>
                      <td className="p-3">
                        {l.kind === "driver_hire"
                          ? <span className="text-[#FFD700] text-xs uppercase tracking-wider">Driver Hire</span>
                          : <span className="text-[#FF5A00] text-xs uppercase tracking-wider">Consignment</span>}
                      </td>
                      <td className="p-3 text-xs">
                        <div>{l.sender_name}</div>
                        <div className="text-zinc-500">{l.sender_city}</div>
                      </td>
                      <td className="p-3">
                        <div>{formatINR(l.budget_inr)}</div>
                        {l.final_price_inr ? <div className="text-xs text-[#FF5A00]">Final {formatINR(l.final_price_inr)}</div> : null}
                      </td>
                      <td className="p-3"><StatusBadge status={l.status} /></td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => deleteLoad(l.id)}
                          data-testid={`admin-delete-load-${l.id}`}
                          className="h-8 rounded-sm border-[#2A2A2A] bg-transparent text-red-400 hover:bg-red-900/20">
                          <Trash2 className="w-3 h-3 mr-1" /> Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {filteredLoads.length === 0 && (
                    <tr><td colSpan={7} className="p-8 text-center text-zinc-500">No loads found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="docs" className="mt-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                { k: "pending", l: "Pending" },
                { k: "approved", l: "Approved" },
                { k: "rejected", l: "Rejected" },
                { k: "all", l: "All" },
              ].map((f) => (
                <button key={f.k} onClick={() => setDocFilter(f.k)} data-testid={`admin-doc-filter-${f.k}`}
                  className={`px-3 py-1 text-xs uppercase tracking-wider border rounded-sm ${docFilter === f.k ? "border-[#FF5A00] bg-[#FF5A00]/10 text-[#FF5A00]" : "border-[#2A2A2A] text-zinc-400 hover:text-white"}`}>
                  {f.l}
                </button>
              ))}
            </div>
            <DocsTable docs={docs} onChange={() => { fetchDocs(docFilter); fetchOverview(); fetchUsers(userRole); }} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit driver dialog */}
      <Dialog open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)}>
        <DialogContent className="bg-[#141414] border-[#2A2A2A] text-white max-w-xl" data-testid="admin-edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit driver {editUser?.name}</DialogTitle>
            <DialogDescription className="text-zinc-400">Only visible fields are editable. DL changes may require re-verification.</DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="grid grid-cols-2 gap-3">
              <F2 label="Name" k="name" u={editUser} s={setEditUser} />
              <F2 label="Phone" k="phone" u={editUser} s={setEditUser} />
              <F2 label="DL Number" k="dl_number" u={editUser} s={setEditUser} />
              <F2 label="Home city" k="home_city" u={editUser} s={setEditUser} />
              <F2 label="Home state" k="home_state" u={editUser} s={setEditUser} />
              <F2 label="Experience (years)" k="experience_years" u={editUser} s={setEditUser} type="number" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="rounded-sm border-[#2A2A2A]">Cancel</Button>
            <Button onClick={saveUserEdit} data-testid="admin-save-user" className="rounded-sm bg-[#FF5A00] hover:bg-[#FF7A2C] text-black font-bold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blacklist dialog */}
      <Dialog open={!!blackUser} onOpenChange={(v) => !v && setBlackUser(null)}>
        <DialogContent className="bg-[#141414] border-rose-500/40 text-white max-w-xl" data-testid="admin-blacklist-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-300">Blacklist {blackUser?.name}?</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Blacklisted users cannot log in or be assigned to new trips. Please record the reason (min 5 characters) — it will be shown on login attempts and on the user's performance card.
            </DialogDescription>
          </DialogHeader>
          <Textarea data-testid="blacklist-reason-input" value={blackReason} onChange={(e) => setBlackReason(e.target.value)}
            rows={4} placeholder="E.g. Delivered damaged cargo on CN NL-2608-0012 and CN NL-2608-0019; refused loading; verbal abuse."
            className="bg-[#0A0A0A] border-[#2A2A2A] mt-2 rounded-sm focus-visible:ring-rose-400" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlackUser(null)} className="rounded-sm border-[#2A2A2A]">Cancel</Button>
            <Button onClick={confirmBlacklist} data-testid="confirm-blacklist-btn" className="rounded-sm bg-rose-600 hover:bg-rose-500 text-white font-bold">Blacklist user</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F2({ label, k, u, s, type = "text" }) {
  return (
    <div>
      <Label className="overline text-zinc-500 text-[10px]">{label}</Label>
      <Input type={type} value={u[k] ?? ""} onChange={(e) => s({ ...u, [k]: e.target.value })}
        data-testid={`admin-edit-${k}`}
        className="mt-1 bg-[#0A0A0A] border-[#2A2A2A] rounded-sm focus-visible:ring-[#FF5A00]" />
    </div>
  );
}

function Stat({ icon: I, label, value, accent }) {
  return (
    <div className={`p-5 border ${accent ? "border-[#FF5A00] bg-[#FF5A00]/10" : "border-[#2A2A2A] bg-[#141414]"}`}>
      <div className="flex items-center gap-2 overline text-zinc-400">
        {I ? <I className="w-3 h-3" /> : null}{label}
      </div>
      <div className={`font-display font-black text-3xl mt-2 ${accent ? "text-[#FF5A00]" : ""}`}>{value}</div>
    </div>
  );
}

function RoleTag({ role }) {
  const map = {
    sender: { bg: "bg-[#FF5A00]/15", color: "text-[#FF5A00]", label: "Sender" },
    lorry_owner: { bg: "bg-blue-900/40", color: "text-blue-300", label: "Lorry Owner" },
    driver: { bg: "bg-[#FFD700]/15", color: "text-[#FFD700]", label: "Driver" },
    admin: { bg: "bg-white/10", color: "text-white", label: "Admin" },
  };
  const s = map[role] || map.sender;
  return <span className={`inline-block ${s.bg} ${s.color} text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm`}>{s.label}</span>;
}

function DocsTable({ docs, onChange }) {
  const token = typeof window !== "undefined" ? localStorage.getItem("ll_token") : "";
  const backend = process.env.REACT_APP_BACKEND_URL;

  const setStatus = async (id, status) => {
    let note = "";
    if (status === "rejected") {
      note = window.prompt("Reason for rejection?") || "";
      if (!note) return;
    }
    try {
      await api.post(`/admin/documents/${id}/status`, { status, review_note: note });
      toast.success(`Document ${status}`);
      onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="border border-[#2A2A2A] rounded-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#141414] text-zinc-400 text-xs uppercase tracking-wider">
          <tr>
            <th className="text-left p-3">Type</th>
            <th className="text-left p-3">Uploader</th>
            <th className="text-left p-3">File</th>
            <th className="text-left p-3">Uploaded</th>
            <th className="text-left p-3">Status</th>
            <th className="text-right p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.id} data-testid={`admin-doc-row-${d.id}`} className="border-t border-[#2A2A2A] hover:bg-[#141414]/60">
              <td className="p-3 uppercase tracking-wider text-xs text-[#FF5A00] font-semibold">{d.doc_type}</td>
              <td className="p-3">
                <div className="text-sm">{d.uploader?.name}</div>
                <div className="text-xs text-zinc-500 font-mono-ll">
                  <RoleTag role={d.uploader?.role} /> · {d.uploader?.email}
                </div>
              </td>
              <td className="p-3">
                <a href={`${backend}/api/documents/${d.id}/download?auth=${encodeURIComponent(token || "")}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid={`admin-doc-view-${d.id}`}
                  className="inline-flex items-center gap-2 text-zinc-300 hover:text-[#FF5A00]">
                  <FileText className="w-4 h-4" /> {d.original_filename}
                </a>
              </td>
              <td className="p-3 text-xs text-zinc-400 font-mono-ll">{new Date(d.created_at).toLocaleString("en-IN")}</td>
              <td className="p-3">
                {d.status === "approved" && <span className="inline-flex items-center gap-1 text-emerald-300 text-xs"><CheckCircle2 className="w-3 h-3" /> Approved</span>}
                {d.status === "rejected" && <span className="inline-flex items-center gap-1 text-red-400 text-xs"><XCircle className="w-3 h-3" /> Rejected</span>}
                {d.status === "pending" && <span className="inline-flex items-center gap-1 text-[#FFD700] text-xs"><Clock className="w-3 h-3" /> Pending</span>}
                {d.review_note ? <div className="text-[10px] text-zinc-500 mt-1">Note: {d.review_note}</div> : null}
              </td>
              <td className="p-3 text-right">
                <div className="inline-flex gap-2">
                  <a href={`${backend}/api/documents/${d.id}/download?auth=${encodeURIComponent(token || "")}`}
                    target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" data-testid={`admin-doc-preview-${d.id}`}
                      className="h-8 rounded-sm border-[#2A2A2A] bg-transparent hover:bg-white/5">
                      <Eye className="w-3 h-3 mr-1" /> View
                    </Button>
                  </a>
                  <Button size="sm" onClick={() => setStatus(d.id, "approved")}
                    data-testid={`admin-doc-approve-${d.id}`}
                    className="h-8 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-black font-bold">
                    <ShieldCheck className="w-3 h-3 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setStatus(d.id, "rejected")}
                    data-testid={`admin-doc-reject-${d.id}`}
                    className="h-8 rounded-sm border-[#2A2A2A] bg-transparent text-red-400 hover:bg-red-900/20">
                    <ShieldAlert className="w-3 h-3 mr-1" /> Reject
                  </Button>
                </div>
              </td>
            </tr>
          ))}
          {docs.length === 0 && (
            <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No documents in this bucket.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
